import React, { useState } from 'react';
import {
  AlertCircle,
  BookOpen,
  Edit3,
  FileText,
  Hash,
  Layers,
  Percent,
  Plus,
  Search
} from 'lucide-react';
import { DataTable, ColumnDef } from '../../../../../components/shared/DataTable/DataTable';
import { MasterDetailLayout } from '../../../../../components/shared/MasterDetail/MasterDetailLayout';
import { RowActionsMenu, viewAction, editAction } from '../../../../../components/shared/RowActionsMenu/RowActionsMenu';
import { DetailPanel } from '../../../../../components/shared/DetailPanel/DetailPanel';
import {
  DetailField,
  DetailFieldList,
  DetailHeaderButton,
  DetailIdentity,
  DetailNotes,
  DetailSection
} from '../../../../../components/shared/DetailPanel/DetailBlocks';
import { SidebarForm } from '../../../../../components/shared/SidebarForm/SidebarForm';
import { FilterSelect } from '../../../../../components/shared/FilterSelect/FilterSelect';
import { useClickOutside, isDetailKeepZone } from '../../../../../hooks/useClickOutside';
import { useToast } from '../../../../../hooks/useToast';
import { PRESET_TAX_TYPES, STORAGE_KEYS, TaxType } from './financeMasters';
import { useStoredList } from './useStoredList';
import '../../shared-modules.css';
import './FinanceMasters.css';

type TaxForm = Omit<TaxType, 'id'>;

const EMPTY_FORM: TaxForm = {
  name: '',
  taxCategory: 'IVA',
  rate: 21,
  repercutidoAccount: '477000',
  soportadoAccount: '472000',
  account: '475100',
  description: '',
  isActive: true
};

const CATEGORY_OPTIONS = [
  { value: 'IVA', label: 'TIPOS DE IVA', icon: Percent },
  { value: 'IRPF', label: 'RETENCIONES IRPF', icon: Percent }
];

const taxAccounts = (t: TaxType) =>
  t.taxCategory === 'IVA'
    ? `Repercutido ${t.repercutidoAccount || '477000'} · Soportado ${t.soportadoAccount || '472000'}`
    : `Retención ${t.account || '475100'}`;

export const FinanceTaxTypesView = () => {
  const { notify } = useToast();

  const [taxTypes, setTaxTypes] = useStoredList<TaxType>(STORAGE_KEYS.taxTypes, PRESET_TAX_TYPES);

  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [isCategoryFilterOpen, setIsCategoryFilterOpen] = useState(false);

  const [selectedTax, setSelectedTax] = useState<TaxType | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState<TaxForm>(EMPTY_FORM);
  const [formError, setFormError] = useState('');

  const setField = <K extends keyof TaxForm>(field: K, value: TaxForm[K]) =>
    setFormData(prev => ({ ...prev, [field]: value }));

  const handleCreateNewClick = () => {
    setSelectedTax(null);
    setFormData(EMPTY_FORM);
    setIsEditing(false);
    setFormError('');
    setIsFormOpen(true);
  };

  const handleEditClick = (tax: TaxType) => {
    const { id, ...rest } = tax;
    setSelectedTax(tax);
    setFormData({
      ...EMPTY_FORM,
      ...rest,
      repercutidoAccount: tax.repercutidoAccount || '477000',
      soportadoAccount: tax.soportadoAccount || '472000',
      account: tax.account || '475100'
    });
    setIsEditing(true);
    setFormError('');
    setIsFormOpen(true);
  };

  const handleSelectTax = (tax: TaxType) => {
    setIsFormOpen(false);
    setSelectedTax(tax);
  };

  const closePanel = () => {
    setIsFormOpen(false);
    setSelectedTax(null);
  };

  // Clic fuera cierra la ficha, el formulario no
  useClickOutside(isDetailKeepZone, closePanel, Boolean(selectedTax) && !isFormOpen);

  useClickOutside(
    (target) => Boolean(target.closest('.filter-select-wrapper')),
    () => setIsCategoryFilterOpen(false),
    isCategoryFilterOpen
  );

  const handleSaveTax = () => {
    if (!formData.name.trim()) {
      setFormError('La denominación del tipo de impuesto es obligatoria.');
      return;
    }
    if (formData.rate < 0 || formData.rate > 100) {
      setFormError('El porcentaje debe estar entre 0 y 100.');
      return;
    }
    const isIva = formData.taxCategory === 'IVA';
    const data: TaxForm = {
      ...formData,
      name: formData.name.trim(),
      description: formData.description.trim(),
      repercutidoAccount: isIva ? formData.repercutidoAccount : '',
      soportadoAccount: isIva ? formData.soportadoAccount : '',
      account: isIva ? '' : formData.account
    };

    if (isEditing && selectedTax) {
      const updated: TaxType = { ...selectedTax, ...data };
      setTaxTypes(prev => prev.map(t => (t.id === updated.id ? updated : t)));
      setSelectedTax(updated);
      notify('Éxito', `Tipo impositivo "${updated.name}" actualizado correctamente.`, 'info');
    } else {
      const baseId = `${data.taxCategory}-${data.rate}`;
      let id = baseId;
      for (let n = 2; taxTypes.some(t => t.id === id); n++) id = `${baseId}-${n}`;
      const created: TaxType = { ...data, id };
      setTaxTypes(prev => [...prev, created]);
      setSelectedTax(created);
      notify('Éxito', `Tipo impositivo "${created.name}" registrado.`, 'info');
    }
    setIsFormOpen(false);
  };

  const query = searchQuery.toLowerCase();
  const filteredTaxes = taxTypes.filter(t =>
    (categoryFilter === 'all' || t.taxCategory === categoryFilter) &&
    (t.name.toLowerCase().includes(query) ||
      t.id.toLowerCase().includes(query) ||
      t.description.toLowerCase().includes(query) ||
      taxAccounts(t).includes(query))
  );

  const columns: ColumnDef<TaxType>[] = [
    {
      id: 'id',
      header: 'CÓDIGO',
      render: (t) => <span className="fin-master__code">{t.id}</span>
    },
    {
      id: 'name',
      header: 'DENOMINACIÓN',
      render: (t) => (
        <>
          <div className="fin-master__name">{t.name}</div>
          <div className="fin-master__desc">{t.description}</div>
        </>
      )
    },
    {
      id: 'taxCategory',
      header: 'CATEGORÍA',
      render: (t) => <span className={`fin-master__type ${t.taxCategory === 'IVA' ? 'good' : 'withholding'}`}>{t.taxCategory}</span>
    },
    {
      id: 'rate',
      header: 'PORCENTAJE',
      align: 'center',
      render: (t) => <span className="fin-master__chip">{t.rate}%</span>
    },
    {
      id: 'accounts',
      header: 'CUENTAS PGC',
      render: (t) => <span className="fin-master__sub">{taxAccounts(t)}</span>
    },
    {
      id: 'isActive',
      header: 'ESTADO',
      align: 'center',
      render: (t) => <span className={`module-badge ${t.isActive ? 'active' : 'inactive'}`}>{t.isActive ? 'Activo' : 'Inactivo'}</span>
    }
  ];

  return (
    <div className="fin-master">
      <div className="module-toolbar">
        <div className="module-search-wrapper">
          <Search className="module-search-icon" size={14} />
          <input
            type="text"
            placeholder="BUSCAR POR TIPO DE IMPUESTO, PORCENTAJE O CUENTAS CONTABLES..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="module-search-input"
          />
        </div>
        <FilterSelect
          isOpen={isCategoryFilterOpen}
          onToggle={setIsCategoryFilterOpen}
          onSelect={setCategoryFilter}
          value={categoryFilter}
          options={CATEGORY_OPTIONS}
          icon={Layers}
          allLabel="TODOS_LOS_IMPUESTOS"
        />
        <button onClick={handleCreateNewClick} className="module-create-btn" data-keep-detail>
          <Plus size={14} /> Nuevo_Tipo_De_Impuesto
        </button>
      </div>

      <MasterDetailLayout isOpen={Boolean(selectedTax || isFormOpen)}>
        <DataTable
          data={filteredTaxes}
          columns={columns}
          title={<><Percent size={14} className="module-panel-title-icon" /> Maestro_De_Tipos_De_Impuestos</>}
          subtitle={`TOTAL: ${filteredTaxes.length}`}
          getRowId={(t) => t.id}
          onRowClick={handleSelectTax}
          selectedRowId={selectedTax?.id}
          emptyText="No se encontraron tipos de impuestos."
          actionsHeader="ACCIONES"
          actionsColumn={(t) => (
            <RowActionsMenu
              actions={[
                viewAction(() => handleSelectTax(t)),
                editAction(() => handleEditClick(t))
              ]}
            />
          )}
        />

        {isFormOpen ? (
          <SidebarForm
            expandable
            title={isEditing ? 'Modificar_Tipo_De_Impuesto' : 'Alta_De_Tipo_De_Impuesto'}
            onClose={closePanel}
            submitLabel="Guardar_Tipo"
            onSubmit={handleSaveTax}
          >
            {formError && (
              <div className="module-form-error">
                <AlertCircle size={14} style={{ flexShrink: 0 }} />
                <span>{formError}</span>
              </div>
            )}

            <DetailSection title="1. Datos_Del_Impuesto" icon={FileText}>
              <div className="sidebar-form-group">
                <label className="sidebar-form-label">Denominación *</label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setField('name', e.target.value)}
                  placeholder="Ej. IVA 21% General"
                  className="sidebar-form-input"
                />
              </div>
              <div className="sidebar-form-grid-2">
                <div className="sidebar-form-group">
                  <label className="sidebar-form-label">Categoría</label>
                  <select
                    value={formData.taxCategory}
                    onChange={(e) => setField('taxCategory', e.target.value as TaxType['taxCategory'])}
                    className="sidebar-form-input"
                  >
                    <option value="IVA">IVA</option>
                    <option value="IRPF">IRPF (Retención)</option>
                  </select>
                </div>
                <div className="sidebar-form-group">
                  <label className="sidebar-form-label">Porcentaje (%) *</label>
                  <input
                    type="number"
                    required
                    min={0}
                    max={100}
                    step="0.01"
                    value={formData.rate}
                    onChange={(e) => setField('rate', parseFloat(e.target.value) || 0)}
                    className="sidebar-form-input"
                  />
                </div>
              </div>
              <div className="sidebar-form-group">
                <label className="sidebar-form-label">Estado</label>
                <select
                  value={formData.isActive ? 'active' : 'inactive'}
                  onChange={(e) => setField('isActive', e.target.value === 'active')}
                  className="sidebar-form-input"
                >
                  <option value="active">ACTIVO</option>
                  <option value="inactive">INACTIVO</option>
                </select>
              </div>
              <div className="sidebar-form-group">
                <label className="sidebar-form-label">Descripción y Ámbito</label>
                <textarea
                  rows={3}
                  value={formData.description}
                  onChange={(e) => setField('description', e.target.value)}
                  placeholder="Operaciones a las que aplica este tipo..."
                  className="sidebar-form-input"
                />
              </div>
            </DetailSection>

            <DetailSection title="2. Cuentas_Contables_PGC" icon={BookOpen}>
              {formData.taxCategory === 'IVA' ? (
                <div className="sidebar-form-grid-2">
                  <div className="sidebar-form-group">
                    <label className="sidebar-form-label">Cuenta IVA Repercutido</label>
                    <input
                      type="text"
                      value={formData.repercutidoAccount}
                      onChange={(e) => setField('repercutidoAccount', e.target.value)}
                      placeholder="477000"
                      className="sidebar-form-input"
                    />
                  </div>
                  <div className="sidebar-form-group">
                    <label className="sidebar-form-label">Cuenta IVA Soportado</label>
                    <input
                      type="text"
                      value={formData.soportadoAccount}
                      onChange={(e) => setField('soportadoAccount', e.target.value)}
                      placeholder="472000"
                      className="sidebar-form-input"
                    />
                  </div>
                </div>
              ) : (
                <div className="sidebar-form-group">
                  <label className="sidebar-form-label">Cuenta de Retención a Cuenta</label>
                  <input
                    type="text"
                    value={formData.account}
                    onChange={(e) => setField('account', e.target.value)}
                    placeholder="475100"
                    className="sidebar-form-input"
                  />
                </div>
              )}
            </DetailSection>
          </SidebarForm>
        ) : selectedTax ? (
          <DetailPanel
            expandable
            title="Ficha_De_Tipo_De_Impuesto"
            onClose={closePanel}
            headerActions={
              <DetailHeaderButton icon={Edit3} label="Editar" onClick={() => handleEditClick(selectedTax)} />
            }
          >
            <DetailIdentity
              icon={Percent}
              reference={selectedTax.id}
              name={selectedTax.name}
              subtitle={
                <span className={`module-badge ${selectedTax.isActive ? 'active' : 'inactive'}`}>
                  {selectedTax.isActive ? 'Activo' : 'Inactivo'}
                </span>
              }
            />

            <DetailFieldList>
              <DetailField icon={Layers} label="CATEGORÍA">{selectedTax.taxCategory}</DetailField>
              <DetailField icon={Hash} label="PORCENTAJE">{`${selectedTax.rate}%`}</DetailField>
              {selectedTax.taxCategory === 'IVA' ? (
                <>
                  <DetailField icon={BookOpen} label="CUENTA_IVA_REPERCUTIDO">{selectedTax.repercutidoAccount || '477000'}</DetailField>
                  <DetailField icon={BookOpen} label="CUENTA_IVA_SOPORTADO">{selectedTax.soportadoAccount || '472000'}</DetailField>
                </>
              ) : (
                <DetailField icon={BookOpen} label="CUENTA_DE_RETENCIÓN">{selectedTax.account || '475100'}</DetailField>
              )}
            </DetailFieldList>

            {selectedTax.description && <DetailNotes title="Descripción y ámbito">{selectedTax.description}</DetailNotes>}
          </DetailPanel>
        ) : null}
      </MasterDetailLayout>
    </div>
  );
};

export default FinanceTaxTypesView;
